import React, { useState } from 'react';
import { siteConfig } from '../data/mock';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Mail, Phone, MapPin, Loader2, Home } from 'lucide-react';
import { characterImages } from '../components/Character';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Locations
const locations = [
  { city: "Riyadh, Saudi Arabia", isHome: true },
  { city: "Doha, Qatar", isHome: false },
  { city: "Cairo, Egypt", isHome: false },
  { city: "London, England", isHome: false },
  { city: "Philadelphia, USA", isHome: false },
  { city: "Tunis, Tunisia", isHome: false }
];

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    services: [],
    projectDescription: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const serviceOptions = [
    { value: "philanthropy", label: "Philanthropy" },
    { value: "tech", label: "Tech" },
    { value: "speaking-media", label: "Speaking and Media" }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok) {
        setIsSubmitted(true);
        setFormData({
          name: '',
          email: '',
          company: '',
          services: [],
          projectDescription: '',
          message: ''
        });
      } else {
        setError(data.detail || 'Something went wrong. Please try again.');
      }
    } catch (err) {
      setError('Unable to send message. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleServiceToggle = (serviceValue) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.includes(serviceValue)
        ? prev.services.filter(s => s !== serviceValue)
        : [...prev.services, serviceValue]
    }));
  };

  return (
    <div className="bg-black pt-24 lg:pt-32">
      {/* Hero Section */}
      <section className="py-14 md:py-24 lg:py-24 relative">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 items-center">
            <div className="lg:col-span-2">
              <p className="font-sans text-[11px] tracking-[0.3em] uppercase text-white/40 mb-6">
                Contact
              </p>
              <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl text-white font-normal leading-[0.95] tracking-tight">
                Let's Connect
              </h1>
              <p className="font-sans text-base text-white/60 leading-[1.8] font-light mt-8 max-w-2xl">
                If your work lives at the intersection of culture, technology, and global impact — we should talk.
              </p>
            </div>
            {/* Character illustration */}
            <div className="hidden lg:flex justify-center">
              <img 
                src={characterImages.peek2}
                alt="Mariam character"
                className="w-64 h-auto drop-shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-14 md:py-24 lg:py-24 border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
            {/* Left Column - Info */}
            <div>
              <p className="font-sans text-[11px] tracking-[0.3em] uppercase text-white/40 mb-4">
                Get in Touch
              </p>
              <h2 className="font-serif text-4xl md:text-5xl text-white font-normal tracking-tight">
                Let's Build Something Intentional
              </h2>

              {/* Contact Info */}
              <div className="space-y-0 mt-12">
                <div className="py-6 border-t border-white/10">
                  <div className="flex items-start gap-4">
                    <Mail size={18} className="text-white/40 mt-1" />
                    <div>
                      <p className="font-sans text-[11px] tracking-[0.15em] uppercase text-white/40 mb-2">
                        Email
                      </p>
                      <a
                        href={`mailto:${siteConfig.email}`}
                        className="font-sans text-base text-white hover:text-white/70 transition-colors duration-300 block"
                      >
                        {siteConfig.email}
                      </a>
                      <a
                        href={`mailto:${siteConfig.ventureEmail}`}
                        className="font-sans text-sm text-white/70 hover:text-white/50 transition-colors duration-300 block mt-1"
                      >
                        {siteConfig.ventureEmail}
                      </a>
                    </div>
                  </div>
                </div>
                <div className="py-6 border-t border-white/10">
                  <div className="flex items-start gap-4">
                    <Phone size={18} className="text-white/40 mt-1" />
                    <div>
                      <p className="font-sans text-[11px] tracking-[0.15em] uppercase text-white/40 mb-2">
                        Phone
                      </p>
                      <a
                        href={`tel:${siteConfig.phone}`}
                        className="font-sans text-base text-white hover:text-white/70 transition-colors duration-300"
                      >
                        {siteConfig.phone}
                      </a>
                    </div>
                  </div>
                </div>
                <div className="py-6 border-t border-white/10">
                  <div className="flex items-start gap-4">
                    <MapPin size={18} className="text-white/40 mt-1" />
                    <div>
                      <p className="font-sans text-[11px] tracking-[0.15em] uppercase text-white/40 mb-3">
                        Locations
                      </p>
                      <div className="space-y-2">
                        {locations.map((loc, index) => (
                          <div key={index} className="flex items-center gap-2">
                            {loc.isHome && <Home size={14} className="text-[#7B1818]" />}
                            <p className={`font-sans text-sm ${loc.isHome ? 'text-white' : 'text-white/70'}`}>
                              {loc.city}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Form */}
            <div className="bg-white/[0.02] border border-white/10 p-8 lg:p-12">
              <h3 className="font-serif text-2xl text-white mb-10 tracking-tight">
                Send a Message
              </h3>
              
              {isSubmitted ? (
                <div className="flex flex-col items-center justify-center py-20">
                  <h3 className="font-serif text-2xl text-white tracking-tight">Message Sent</h3>
                  <p className="font-sans text-sm text-white/50 mt-3 text-center font-light">
                    Thank you for reaching out. I'll be in touch soon.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-8">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="font-sans text-[11px] tracking-[0.15em] uppercase text-white/40 block mb-3">
                        Name *
                      </label>
                      <Input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="bg-transparent border-white/20 text-white placeholder:text-white/30 focus:border-white/50 rounded-none h-12 font-sans text-sm"
                        placeholder="Your name"
                      />
                    </div>
                    <div>
                      <label className="font-sans text-[11px] tracking-[0.15em] uppercase text-white/40 block mb-3">
                        Email *
                      </label>
                      <Input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="bg-transparent border-white/20 text-white placeholder:text-white/30 focus:border-white/50 rounded-none h-12 font-sans text-sm"
                        placeholder="your@email.com"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="font-sans text-[11px] tracking-[0.15em] uppercase text-white/40 block mb-3">
                      Company / Organization
                    </label>
                    <Input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      className="bg-transparent border-white/20 text-white placeholder:text-white/30 focus:border-white/50 rounded-none h-12 font-sans text-sm"
                      placeholder="Company name"
                    />
                  </div>

                  {/* Services Selection */}
                  <div>
                    <label className="font-sans text-[11px] tracking-[0.15em] uppercase text-white/40 block mb-4">
                      What are you interested in? (Select all that apply)
                    </label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {serviceOptions.map((option) => (
                        <label
                          key={option.value}
                          className={`flex items-center gap-3 p-3 border cursor-pointer transition-colors ${
                            formData.services.includes(option.value)
                              ? 'border-white/50 bg-white/5'
                              : 'border-white/10 hover:border-white/30'
                          }`}
                        >
                          <Checkbox
                            checked={formData.services.includes(option.value)}
                            onCheckedChange={() => handleServiceToggle(option.value)}
                            className="border-white/30 data-[state=checked]:bg-white data-[state=checked]:text-black"
                          />
                          <span className="font-sans text-xs text-white/70">{option.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Cultural Project Description */}
                  {formData.services.includes('cultural-project') && (
                    <div>
                      <label className="font-sans text-[11px] tracking-[0.15em] uppercase text-white/40 block mb-3">
                        Tell us about your cultural project
                      </label>
                      <Textarea
                        name="projectDescription"
                        value={formData.projectDescription}
                        onChange={handleChange}
                        rows={4}
                        className="bg-transparent border-white/20 text-white placeholder:text-white/30 focus:border-white/50 rounded-none resize-none font-sans text-sm"
                        placeholder="Describe your cultural initiative, what you're working on, and how we might collaborate..."
                      />
                    </div>
                  )}

                  <div>
                    <label className="font-sans text-[11px] tracking-[0.15em] uppercase text-white/40 block mb-3">
                      Message *
                    </label>
                    <Textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      className="bg-transparent border-white/20 text-white placeholder:text-white/30 focus:border-white/50 rounded-none resize-none font-sans text-sm"
                      placeholder="Tell me about your project or inquiry..."
                    />
                  </div>

                  {error && (
                    <div className="p-4 bg-red-900/20 border border-red-500/30 text-red-300 text-sm">
                      {error}
                    </div>
                  )}

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full h-14 bg-gradient-to-r from-[#7B1818] to-[#4A0E0E] hover:from-[#8B2020] hover:to-[#5A1010] text-white font-sans text-[11px] tracking-[0.2em] uppercase rounded-none disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center justify-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Sending...
                      </span>
                    ) : (
                      'Send Message'
                    )}
                  </Button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
